/**
 * A/B Testing Service
 *
 * DB-driven A/B testing with localStorage persistence and analytics integration.
 * Tests are fetched from the `ab_test` table via tRPC and cached in memory.
 * Hardcoded defaults are used as fallback if the fetch hasn't completed yet.
 */

export type ABTestVariant = 'control' | 'variant';

export interface ABTestConfig {
  key: string;
  name: string;
  description: string | null;
  controlWeight: number;
  variantWeight: number;
}

/**
 * Hardcoded fallback tests — used only until DB config is loaded.
 * Keep these in sync with the DB for a seamless first-load experience.
 */
const FALLBACK_TESTS: Record<string, ABTestConfig> = {
  publicWorkspaceLink: {
    key: 'publicWorkspaceLink',
    name: 'public-workspace-link',
    description: 'Show "Publica tu espacio" link in navbar',
    controlWeight: 50,
    variantWeight: 50,
  },
  insightsPaywall: {
    key: 'insightsPaywall',
    name: 'insights-paywall',
    description: 'Paywall style for workspace insights',
    controlWeight: 50,
    variantWeight: 50,
  },
};

/** In-memory cache of DB tests, keyed by test key */
let testsCache: Record<string, ABTestConfig> | null = null;

/**
 * Load tests from DB response into the in-memory cache.
 * Called by the useABTestConfig hook after fetching from tRPC.
 */
export function setABTestsFromDB(tests: ABTestConfig[]): void {
  testsCache = {};
  for (const test of tests) {
    testsCache[test.key] = test;
  }
}

/**
 * Get config for a specific test. DB cache takes priority, then fallback.
 * Returns null if the test doesn't exist anywhere.
 */
function getTestConfig(testKey: string): ABTestConfig | null {
  if (testsCache?.[testKey]) return testsCache[testKey];
  if (FALLBACK_TESTS[testKey]) return FALLBACK_TESTS[testKey];
  return null;
}

/**
 * Get all known test keys (union of DB cache + fallbacks).
 */
function getAllTestKeys(): string[] {
  const keys = new Set<string>(Object.keys(FALLBACK_TESTS));
  if (testsCache) {
    for (const key of Object.keys(testsCache)) {
      keys.add(key);
    }
  }
  return Array.from(keys);
}

/**
 * Get user's assigned variant for a specific test.
 * Persists in localStorage for consistent experience. Returns 'control' for SSR.
 */
export function getABTestVariant(testKey: string): ABTestVariant {
  if (typeof window === 'undefined') return 'control';

  const test = getTestConfig(testKey);
  if (!test) return 'control';

  const storageKey = `ab-test-${test.key}`;

  // Check if user already has an assigned variant
  const storedVariant = localStorage.getItem(storageKey);
  if (storedVariant === 'control' || storedVariant === 'variant') {
    return storedVariant;
  }

  // Assign new variant based on weights
  const total = test.controlWeight + test.variantWeight;
  const random = Math.random() * total;
  const variant: ABTestVariant = random < test.variantWeight ? 'variant' : 'control';

  localStorage.setItem(storageKey, variant);
  return variant;
}

/**
 * Force a specific variant for a test (useful for testing/debugging).
 */
export function forceABTestVariant(testKey: string, variant: ABTestVariant): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(`ab-test-${testKey}`, variant);
}

/**
 * Clear variant assignment for a test (user will be re-assigned on next visit).
 */
export function clearABTestVariant(testKey: string): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(`ab-test-${testKey}`);
}

/**
 * Get all active tests and their current variants.
 */
export function getAllABTestVariants(): Record<string, ABTestVariant> {
  if (typeof window === 'undefined') return {};

  const variants: Record<string, ABTestVariant> = {};
  for (const key of getAllTestKeys()) {
    variants[key] = getABTestVariant(key);
  }
  return variants;
}

// ── Server-side (SSR) assignment ─────────────────────────────────────────────────────
//
// The localStorage path above decides on the CLIENT, so during SSR every test renders
// 'control' and flips after hydration. That is fine for details behind a click, but a test
// that changes the first paint (e.g. which floating chat entry the page shows) would
// visibly swap — so those tests are assigned SERVER-side, from the same `ab_test` config
// (weights = the split; `is_active=false` or `variant_weight=0` = off, no deploy needed),
// with a sticky per-test bucket COOKIE instead of localStorage:
//
//  - Sticky: same visitor, same variant across pages/visits; clearing cookies re-rolls.
//  - Monotonic ramp-up: raising variant_weight only ADDS buckets to the variant; nobody
//    already in it is reassigned (their state — e.g. a WhatsApp thread — survives).
//  - Fail-closed: row missing/inactive, degenerate weights, or a call-site `disabled`
//    guard → 'control'. A config mistake turns the experiment off, never everyone-in.
//
// The loader passes the resolved variant down via loader data; components never call
// `getABTestVariant` for a server-assigned test (that would create a second, conflicting
// localStorage assignment).

/** The WhatsApp-agent chat-entry rollout: 'variant' sees the WhatsApp button INSTEAD of the
 * web ChatWidget (one chat door per visitor). Assigned in root.tsx's loader. */
export const WA_CHAT_ENTRY_TEST_KEY = 'waChatEntry';

const BUCKET_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

export function abBucketCookieName(testKey: string): string {
  return `ab-bucket-${testKey}`;
}

/** Bucket (0–99) from the Cookie header — `null` for absent/garbage/out-of-range, which
 * makes the caller assign a fresh one. */
export function parseAbBucket(cookieHeader: string | null, testKey: string): number | null {
  if (!cookieHeader) return null;
  const name = abBucketCookieName(testKey).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = cookieHeader.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  if (!match) return null;
  const raw = match[1].trim();
  if (!/^\d{1,2}$/.test(raw)) return null; // integers 0–99 only
  return Number(raw);
}

export function serializeAbBucketCookie(testKey: string, bucket: number): string {
  return `${abBucketCookieName(testKey)}=${bucket}; Path=/; Max-Age=${BUCKET_COOKIE_MAX_AGE}; SameSite=Lax`;
}

/** The variant's share (0–100) from the DB weights. Degenerate weights (zero/negative) mean
 * OFF — a bad row must never flip everyone into the variant. */
export function variantPercent(config: ABTestConfig): number {
  const control = config.controlWeight;
  const variant = config.variantWeight;
  if (variant <= 0 || control < 0 || control + variant <= 0) return 0;
  return Math.min(100, (variant / (control + variant)) * 100);
}

/**
 * The one call an SSR loader makes for a server-assigned test. `tests` is the ACTIVE list
 * (`abTest.getActiveTests` — the same query the client cache hydrates from, fetched once in
 * the loader), so an `is_active=false` or deleted row simply isn't found → control.
 * `disabled` lets the call site force control when the variant cannot work (e.g. the
 * WhatsApp number isn't configured) — the bucket is still assigned/kept, so enabling later
 * never re-rolls anyone.
 */
export function resolveServerABTestVariant(args: {
  testKey: string;
  cookieHeader: string | null;
  tests: ABTestConfig[];
  disabled?: boolean;
}): { bucket: number; assigned: boolean; variant: ABTestVariant } {
  const existing = parseAbBucket(args.cookieHeader, args.testKey);
  const bucket = existing ?? Math.floor(Math.random() * 100);
  const config = args.tests.find((t) => t.key === args.testKey);
  const variant: ABTestVariant =
    !args.disabled && config && bucket < variantPercent(config) ? 'variant' : 'control';
  return { bucket, assigned: existing === null, variant };
}

/**
 * Status of the WhatsApp chat-entry rollout, for the admin cockpit banner. `percent` is the
 * configured variant share (what the DB row says); `paused` is whether visitors can actually
 * see the button right now — false only when the row is active with a positive share AND the
 * public WhatsApp number is configured (without it the button renders null for everyone, and
 * showing "1%" while nobody can see it would lie to the admin).
 */
export function waRolloutStatus(
  tests: ABTestConfig[],
  publicNumberConfigured: boolean,
): { percent: number; paused: boolean } {
  const config = tests.find((t) => t.key === WA_CHAT_ENTRY_TEST_KEY);
  const percent = config ? Math.round(variantPercent(config) * 10) / 10 : 0;
  return { percent, paused: percent <= 0 || !publicNumberConfigured };
}

/**
 * Track A/B test impression (when user sees the test).
 */
export function trackABTestImpression(testKey: string, variant: ABTestVariant): void {
  if (typeof window === 'undefined') return;

  const test = getTestConfig(testKey);

  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'ab_test_impression', {
      test_name: test?.name ?? testKey,
      test_key: testKey,
      variant,
      description: test?.description ?? '',
    });
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('[A/B Test] Impression:', { test: testKey, variant, description: test?.description });
  }
}

/**
 * Track A/B test conversion (when user takes desired action).
 */
export function trackABTestConversion(
  testKey: string,
  variant: ABTestVariant,
  action: string,
  value?: number
): void {
  if (typeof window === 'undefined') return;

  const test = getTestConfig(testKey);

  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'ab_test_conversion', {
      test_name: test?.name ?? testKey,
      test_key: testKey,
      variant,
      action,
      value,
      description: test?.description ?? '',
    });
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('[A/B Test] Conversion:', { test: testKey, variant, action, value });
  }
}

/**
 * Debug utility: Log all active tests and their configs.
 */
export function debugABTests(): void {
  if (typeof window === 'undefined') return;

  console.group('🧪 A/B Tests');
  console.log('Source:', testsCache ? 'database' : 'fallback');

  for (const key of getAllTestKeys()) {
    const config = getTestConfig(key);
    const variant = getABTestVariant(key);
    console.log(`${key}:`, {
      variant,
      description: config?.description,
      weights: { control: config?.controlWeight, variant: config?.variantWeight },
    });
  }

  console.groupEnd();
}

// Type declaration for gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    abTests?: {
      debug: typeof debugABTests;
      getAll: typeof getAllABTestVariants;
      force: typeof forceABTestVariant;
      clear: typeof clearABTestVariant;
    };
  }
}

// Export for easy access in browser console
if (typeof window !== 'undefined') {
  window.abTests = {
    debug: debugABTests,
    getAll: getAllABTestVariants,
    force: forceABTestVariant,
    clear: clearABTestVariant,
  };
}
